import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Plus, Search, Mail, Phone, Trash2, Pencil, Building2, Upload, Loader2, X, Users, Tag,
} from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Avatar } from "../components/Avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const EMPTY = { name: "", email: "", phone: "", title: "", company_name: "", group: "", status: "qualified", notes: "", avatar_url: "", company_ids: [] };
const STATUSES = [["to_qualify", "À qualifier", "#F59E0B"], ["qualified", "Qualifié", "#10B981"], ["prospect", "Prospect", "#3B82F6"], ["client", "Client", "#8B5CF6"], ["archived", "Archivé", "#71717A"]];
const statusMeta = (s) => STATUSES.find((x) => x[0] === s) || STATUSES[1];

function ContactCard({ c, onEdit, onDel }) {
  const sm = statusMeta(c.status);
  return (
    <div data-testid={`contact-${c.id}`} className="group bg-card border border-white/10 rounded-xl p-4 hover:border-white/30 transition-colors">
      <div className="flex items-start gap-3">
        <Avatar name={c.name} src={c.avatar_url} color="#27272A" size={48} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">{c.name}</div>
          {c.title && <div className="text-xs text-klein truncate">{c.title}</div>}
          {c.company_name && <div className="text-xs text-zinc-400 truncate flex items-center gap-1"><Building2 size={11} /> {c.company_name}</div>}
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button data-testid={`edit-contact-${c.id}`} onClick={() => onEdit(c)} className="p-1.5 rounded-md hover:bg-white/10 text-zinc-400"><Pencil size={14} /></button>
          <button data-testid={`delete-contact-${c.id}`} onClick={() => onDel(c)} className="p-1.5 rounded-md hover:bg-signal/10 text-zinc-400 hover:text-signal"><Trash2 size={14} /></button>
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-xs text-zinc-300 hover:text-klein"><Mail size={13} className="text-klein" /> <span className="truncate">{c.email}</span></a>}
        {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-xs text-zinc-300 hover:text-klein"><Phone size={13} className="text-klein" /> {c.phone}</a>}
      </div>
      <div className="mt-3 flex flex-wrap gap-1 items-center">
        <span data-testid={`contact-status-${c.id}`} className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ background: sm[2] + "22", color: sm[2] }}>{sm[1]}</span>
        {c.group && c.group !== sm[1] && <span className="text-[10px] font-mono-label px-1.5 py-0.5 rounded bg-klein/15 text-klein">{c.group}</span>}
        {(c.shared_with || []).map((s, i) => (
          <span key={i} className="text-[10px] font-mono-label px-1.5 py-0.5 rounded bg-white/[0.06] text-zinc-400 flex items-center gap-0.5"><Building2 size={9} /> {s}</span>
        ))}
      </div>
    </div>
  );
}

export default function ContactsPage() {
  const { managed, selectedId } = useCompany();
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("all");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedId && selectedId !== "all" ? `?company_id=${selectedId}` : "";
      const { data } = await api.get(`/contacts${params}`);
      setContacts(data);
    } finally { setLoading(false); }
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);

  const groups = Array.from(new Set(contacts.map((c) => c.group).filter(Boolean)));
  const visible = contacts
    .filter((c) => (group === "all" ? true : c.group === group))
    .filter((c) => {
      const q = search.toLowerCase();
      return !q || [c.name, c.email, c.company_name, c.title, c.phone].some((f) => (f || "").toLowerCase().includes(q));
    });

  const del = async (c) => {
    if (!window.confirm(`Supprimer le contact « ${c.name} » ?`)) return;
    await api.delete(`/contacts/${c.id}`);
    toast.success("Contact supprimé");
    load();
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="h-14 border-b border-white/10 bg-card flex items-center justify-between px-4 lg:px-6 shrink-0 gap-3">
        <div className="flex items-center gap-2 flex-1 max-w-md bg-white/[0.06] rounded-md px-3 py-1.5">
          <Search size={16} className="text-zinc-400 shrink-0" />
          <input data-testid="contacts-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un contact…"
            className="flex-1 bg-transparent text-sm focus:outline-none" />
        </div>
        <button data-testid="new-contact-button" onClick={() => setEditing({ ...EMPTY, company_ids: selectedId && selectedId !== "all" ? [selectedId] : (managed[0] ? [managed[0].id] : []) })}
          className="flex items-center gap-1.5 bg-klein text-zinc-950 px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 shrink-0"><Plus size={16} /> Nouveau contact</button>
      </div>

      {/* Group filters */}
      <div className="px-4 lg:px-6 py-2 border-b border-white/10 bg-card flex items-center gap-2 overflow-x-auto no-scrollbar">
        <button data-testid="group-all" onClick={() => setGroup("all")} className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${group === "all" ? "bg-klein text-zinc-950" : "bg-white/[0.06] text-zinc-400"}`}>Tous ({contacts.length})</button>
        {groups.map((g) => (
          <button key={g} data-testid={`group-${g}`} onClick={() => setGroup(g)} className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1 ${group === g ? "bg-klein text-zinc-950" : "bg-white/[0.06] text-zinc-400"}`}>
            <Tag size={11} /> {g}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        {loading ? (
          <div className="p-12 flex justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-zinc-400">
            <Users size={56} strokeWidth={1} />
            <p className="mt-4 text-sm">Aucun contact. Créez votre répertoire partagé.</p>
          </div>
        ) : group !== "all" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {visible.map((c) => <ContactCard key={c.id} c={c} onEdit={setEditing} onDel={del} />)}
          </div>
        ) : (
          (() => {
            const secs = [];
            const idx = {};
            visible.forEach((c) => { const g = c.group || "Sans groupe"; if (idx[g] === undefined) { idx[g] = secs.length; secs.push({ g, items: [] }); } secs[idx[g]].items.push(c); });
            secs.sort((a, b) => (a.g === "Sans groupe" ? 1 : b.g === "Sans groupe" ? -1 : a.g.localeCompare(b.g)));
            return (
              <div className="space-y-6">
                {secs.map((s) => (
                  <div key={s.g} data-testid={`contact-section-${s.g}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Tag size={14} className="text-klein" />
                      <h3 className="font-cabinet font-bold text-sm uppercase tracking-wide">{s.g}</h3>
                      <span className="text-xs text-zinc-500 tabular-nums">{s.items.length}</span>
                      <div className="flex-1 h-px bg-white/[0.06]" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {s.items.map((c) => <ContactCard key={c.id} c={c} onEdit={setEditing} onDel={del} />)}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()
        )}
      </div>

      {editing && <ContactDialog contact={editing} managed={managed} groups={groups} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ContactDialog({ contact, managed, groups, onClose, onSaved }) {
  const [form, setForm] = useState(contact);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef();
  const isEdit = !!contact.id;

  const toggleCompany = (id) => {
    setForm((f) => ({ ...f, company_ids: f.company_ids.includes(id) ? f.company_ids.filter((x) => x !== id) : [...f.company_ids, id] }));
  };

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, avatar_url: fileUrl(data.id) }));
    } catch { toast.error("Échec de l'envoi"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    if (!form.company_ids.length) { toast.error("Sélectionnez au moins une entreprise"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/contacts/${contact.id}`, form);
      else await api.post("/contacts", form);
      toast.success(isEdit ? "Contact mis à jour" : "Contact créé");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contact-dialog">
        <DialogHeader><DialogTitle className="font-cabinet">{isEdit ? "Modifier le contact" : "Nouveau contact"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Avatar name={form.name} src={form.avatar_url} color="#27272A" size={56} />
            <button data-testid="contact-photo" onClick={() => photoRef.current.click()} disabled={uploading} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-white/10 hover:border-white/30">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Photo
            </button>
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
          </div>
          <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom complet *" className="w-full border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            <input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone" className="border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            <input data-testid="contact-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Fonction" className="border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            <input data-testid="contact-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Société du contact" className="border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          </div>
          <div>
            <input data-testid="contact-group" list="groups-list" value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} placeholder="Groupe (ex: Clients, Fournisseurs, Prospects)" className="w-full border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            <datalist id="groups-list">{groups.map((g) => <option key={g} value={g} />)}</datalist>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Statut</label>
            <div className="flex flex-wrap gap-1.5">
              {STATUSES.map(([v, l, col]) => (
                <button key={v} data-testid={`contact-status-opt-${v}`} onClick={() => setForm({ ...form, status: v })}
                  className="px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors"
                  style={form.status === v ? { background: col + "22", color: col, borderColor: col } : { borderColor: "rgba(127,127,127,0.2)", color: "#a1a1aa" }}>{l}</button>
              ))}
            </div>
          </div>
          <textarea data-testid="contact-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" className="w-full border border-white/10 rounded-md px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Partagé avec (entreprises)</label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {managed.map((c) => (
                <button key={c.id} data-testid={`share-company-${c.id}`} onClick={() => toggleCompany(c.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${form.company_ids.includes(c.id) ? "bg-klein text-zinc-950 border-klein" : "bg-card text-zinc-400 border-white/10 hover:border-white/30"}`}>
                  <Building2 size={12} /> {c.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-white/10 text-sm font-medium">Annuler</button>
          <button data-testid="save-contact-button" onClick={save} disabled={saving} className="px-4 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium">{saving ? "…" : "Enregistrer"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
